import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../utils/websocket';
import { useNotifications, NotificationMessage } from '../contexts/NotificationsContext';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import AccessRequestResponseModal from './AccessRequestResponseModal';

interface NotificationCenterProps {
  baseUrl: string;
  token: string;
  userId: string;
  deviceId: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  baseUrl,
  token,
  userId,
  deviceId
}) => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAsReadLocal, markAllAsRead, markAllAsReadLocal } = useNotifications();
  const { wsClient } = useWebSocket(baseUrl, token, userId, deviceId);
  
  // Listen for read status updates from other clients
  useEffect(() => {
    if (!wsClient) return;
    
    const handleReadStatusUpdate = (data: any) => {
      if (data.all_read) {
        // All notifications marked as read (from another client via WebSocket)
        markAllAsReadLocal(); // Use local-only update to avoid unnecessary API call
      } else if (data.notification_id) {
        // Single notification marked as read (from another client via WebSocket)
        markAsReadLocal(data.notification_id); // Use local-only update to avoid unnecessary API call
      }
    };
    
    // Register handler for read status updates
    wsClient.onMessage('notification_read_status', handleReadStatusUpdate);
    
    // Cleanup is handled by WebSocketClient's messageHandlers Map
  }, [wsClient, markAsReadLocal, markAllAsReadLocal]);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown when pressing ESC
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Helper function to format file name (remove .zip extension only for folders)
  const formatFileName = (fileName: string, assetType?: string): string => {
    // Only remove .zip extension if asset_type is explicitly 'folder'
    // Regular zip files should keep their .zip extension
    if (assetType?.toLowerCase() === 'folder' && fileName.toLowerCase().endsWith('.zip')) {
      return fileName.slice(0, -4); // Remove .zip extension
    }
    return fileName;
  };

  const getNotificationTitle = (type: string, payload?: any): string => {
    const actualType = payload?.type || type;
    switch (actualType) {
      case 'file_shared':
      case 'folder_shared':
        return t('types.assetShared');
      case 'file_access_revoked':
        return t('types.accessRevoked');
      case 'file_access_violation':
        return t('types.accessViolation');
      case 'file_access_request':
      case 'access_count_limit_request':
      case 'access_period_limit_request':
      case 'asset_permissions_request':
        return t('types.accessRequest');
      case 'access_request_responded':
        return t('types.accessRequestResponse');
      case 'access_request_canceled':
        return t('types.accessRequestCanceled');
      case 'policy_updated':
        return t('types.policyUpdated');
      case 'user_removed_from_group':
        return t('types.removedFromGroup');
      case 'system_announcement':
        return t('types.systemAnnouncement');
      case 'infrastructure_provisioning':
        return payload?.status === 'ready' ? t('types.infrastructureReady') : t('types.infrastructureProvisioning');
      case 'license_alert':
        return payload?.reason === 'expiry' ? t('licenseAlert.licenseExpiringSoon') : t('licenseAlert.lowLicenseCount');
      default:
        return t('types.notification');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            {t('status.pending')}
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {t('status.accepted')}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            {t('status.rejected')}
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            {t('status.canceled')}
          </span>
        );
      default:
        return null;
    }
  };

  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

  // Get file icon helper function
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase() || 'file';
    const upperExtension = extension.toUpperCase();
    
    // Map extensions to icon file names (handles variations like docx -> doc)
    const extensionMap: Record<string, string> = {
      'txt': 'txt',
      'doc': 'doc',
      'docx': 'doc',
      'xls': 'xls',
      'xlsx': 'xls',
      'ppt': 'ppt',
      'pptx': 'ppt',
      'pdf': 'pdf',
      'csv': 'csv',
      'png': 'png',
      'jpg': 'jpg',
      'jpeg': 'jpg',
    };
    
    // Check if we have an icon for this extension
    const iconName = extensionMap[extension];
    if (iconName) {
      return (
        <img 
          src={`/asset-icons/${iconName}.webp`} 
          alt={`${extension} file`}
          className="w-6 h-6 flex-shrink-0 object-contain"
        />
      );
    }
    
    // Fallback to text-based icon for unsupported types
    return (
      <div className="w-6 h-6 flex-shrink-0 bg-black dark:bg-gray-800 rounded flex items-center justify-center">
        <span className="text-white text-xs font-bold">{upperExtension}</span>
      </div>
    );
  };

  // Filter notifications: replace pending requests with canceled ones (file owner side only)
  const processedNotifications = useMemo(() => {
    const result: NotificationMessage[] = [];
    const pendingRequestIds = new Set<string>();
    const canceledRequestIds = new Map<string, NotificationMessage>(); // access_request_id -> canceled notification

    // First pass: collect pending requests and canceled notifications
    notifications.forEach(n => {
      const actualType = n.payload?.type || n.type;
      const np = n.payload?.payload || n.payload;
      const requestId = np?.access_request_id || n.payload?.access_request_id;
      
      if (actualType === 'access_request_canceled' && requestId) {
        // This is a canceled notification - store it
        canceledRequestIds.set(requestId, n);
      } else if ((actualType === 'file_access_request' || 
                  actualType === 'access_count_limit_request' || 
                  actualType === 'access_period_limit_request' || 
                  actualType === 'asset_permissions_request') && 
                 requestId) {
        // Check if this is a pending request
        const status = np?.status || n.payload?.status;
        if (!status || status === 'pending') {
          pendingRequestIds.add(requestId);
        }
      }
    });

    // Second pass: build result, replacing pending with canceled when both exist
    notifications.forEach(n => {
      const actualType = n.payload?.type || n.type;
      const np = n.payload?.payload || n.payload;
      const requestId = np?.access_request_id || n.payload?.access_request_id;
      
      if (actualType === 'access_request_canceled' && requestId) {
        // If there's a pending request for this, skip the canceled notification
        // (we'll replace the pending one instead)
        if (!pendingRequestIds.has(requestId)) {
          result.push(n);
        }
      } else if ((actualType === 'file_access_request' || 
                  actualType === 'access_count_limit_request' || 
                  actualType === 'access_period_limit_request' || 
                  actualType === 'asset_permissions_request') && 
                 requestId) {
        const status = np?.status || n.payload?.status;
        if (!status || status === 'pending') {
          // Check if there's a canceled notification for this request
          if (canceledRequestIds.has(requestId)) {
            // Replace pending with canceled notification
            result.push(canceledRequestIds.get(requestId)!);
          } else {
            result.push(n);
          }
        } else {
          // Not pending, add as-is
          result.push(n);
        }
      } else {
        // Not an access request notification, add as-is
        result.push(n);
      }
    });

    return result;
  }, [notifications]);

  // Extract all access request IDs from notifications to check their status
  const accessRequestIds = useMemo(() => {
    const ids = new Set<string>();
    notifications.forEach(n => {
      const actualType = n.payload?.type || n.type;
      if (actualType === 'file_access_request' || 
          actualType === 'access_count_limit_request' || 
          actualType === 'access_period_limit_request' || 
          actualType === 'asset_permissions_request') {
        const np = n.payload?.payload || n.payload;
        const requestId = np?.access_request_id || n.payload?.access_request_id;
        if (requestId) {
          ids.add(requestId);
        }
      }
    });
    return Array.from(ids);
  }, [notifications]);

  // Fetch access request statuses for all pending notifications (bulk)
  const { data: accessRequestsStatus } = useQuery({
    queryKey: ['access-requests-status', accessRequestIds],
    queryFn: async () => {
      const statusMap: Record<string, string> = {};
      if (accessRequestIds.length === 0) {
        return statusMap;
      }
      try {
        const requests = await apiService.getBulkAccessRequests(accessRequestIds);
        requests.forEach((request: any) => {
          // Get status and ensure it's a string, normalize to lowercase
          const status = String(request.status || request.Status || 'pending').toLowerCase();
          statusMap[request.id] = status;
          console.log(`📊 Access request ${request.id} status:`, request.status, '→ normalized:', status);
        });
        // For any IDs not returned (not found), assume pending
        accessRequestIds.forEach((id) => {
          if (!statusMap[id]) {
            statusMap[id] = 'pending';
          }
        });
      } catch (error) {
        // If bulk fetch fails, assume all pending
        console.error(`❌ Failed to fetch bulk access requests:`, error);
        accessRequestIds.forEach((id) => {
          statusMap[id] = 'pending';
        });
      }
      return statusMap;
    },
    enabled: accessRequestIds.length > 0,
    staleTime: 0, // Don't cache - always fetch fresh data
    gcTime: 0, // Don't keep in cache after unmount (formerly cacheTime)
  });

  const renderNotification = (notification: NotificationMessage) => {
    const { type, payload, id, read, timestamp } = notification;
    // Extract actual type from payload if available (backend sends type in payload)
    // Payload structure: { type, payload: { access_request_id, file_id, ... }, ... }
    const actualType = payload?.type || type;
    const notificationPayload = payload?.payload || payload; // Get nested payload if exists
    // Always use localized title (ignore backend title to avoid showing English when UI is in another language)
    const title = getNotificationTitle(actualType, payload);

    return (
      <div
        key={id || `notification-${Date.now()}-${Math.random()}`}
        onClick={() => {
          if (id && !read) {
            markAsRead(id);
          }
          // Handle click navigation for infrastructure notifications
          if (actualType === 'infrastructure_provisioning' && notificationPayload?.action_url) {
            setIsOpen(false);
            navigate(notificationPayload.action_url);
          }
        }}
        className={`p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer overflow-hidden ${
          !read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
        }`}
      >
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              {actualType === 'infrastructure_provisioning' ? (
                // Show spinner for provisioning, checkmark for ready
                notificationPayload?.status === 'ready' ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : notificationPayload?.status === 'failed' ? (
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )
              ) : !read ? (
                <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
              ) : null}
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">{title}</h4>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatTimestamp(timestamp || payload?.created_at || payload?.timestamp || Date.now() / 1000)}
            </span>
          </div>
            
            {(type === 'file_shared' || type === 'folder_shared') && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  <strong className="break-all">{formatFileName(
                    notificationPayload?.file_name || payload?.file_name || payload?.payload?.file_name || '',
                    notificationPayload?.asset_type || payload?.asset_type || payload?.payload?.asset_type
                  )}</strong> has been shared with you by{' '}
                  <strong>{notificationPayload?.shared_by_email || payload?.shared_by_email || payload?.payload?.shared_by_email || notificationPayload?.shared_by_name || payload?.shared_by_name || payload?.payload?.shared_by_name || 'Someone'}</strong>
                </p>
              </div>
            )}

            {type === 'file_access_violation' && (
              <div className="mt-2">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 min-w-0">
                  <div className="flex-1 flex items-start gap-2 min-w-0 overflow-hidden">
                    {(payload?.file_name || payload?.payload?.file_name) && (
                      <div className="mt-0.5 flex-shrink-0">
                        {getFileIcon(payload?.file_name || payload?.payload?.file_name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                        <strong>{payload?.violated_by_email || payload?.payload?.violated_by_email}</strong> attempted to access your file{' '}
                        <strong className="break-all">{payload?.file_name || payload?.payload?.file_name}</strong> but was denied
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                        {payload?.violation_type || payload?.payload?.violation_type}
                      </p>
                    </div>
                  </div>
                  {/* Show Manage File button if file_id is available (either at top level or in nested payload) */}
                  {(payload?.file_id || payload?.payload?.file_id) && (
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        const fileId = payload?.file_id || payload?.payload?.file_id;
                        navigate(`/assets/${fileId}/manage`, { replace: false });
                        // Force reload if already on manage page
                        window.location.href = `/assets/${fileId}/manage`;
                      }}
                      className="text-xs border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap transition-colors flex-shrink-0 self-start sm:self-auto"
                    >
                      Manage File
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Access Request - File Owner View (pending requests) */}
            {(actualType === 'file_access_request' || 
              actualType === 'access_count_limit_request' || 
              actualType === 'access_period_limit_request' || 
              actualType === 'asset_permissions_request') && (
              <div className="mt-2">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 min-w-0">
                  <div className="flex-1 flex items-start gap-2 min-w-0 overflow-hidden">
                    {(notificationPayload?.file_name || payload?.file_name) && (
                      <div className="mt-0.5 flex-shrink-0">
                        {getFileIcon(notificationPayload?.file_name || payload?.file_name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      {/* Show detailed message (from notification.message) - this is more detailed */}
                      {notification.message ? (
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                          {notification.message}
                        </p>
                      ) : (
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                          <strong>{notificationPayload?.requester_email || payload?.requester_email || payload?.user_email || payload?.requested_by_email}</strong> is requesting access to your file{' '}
                          <strong className="break-all">{notificationPayload?.file_name || payload?.file_name}</strong>
                        </p>
                      )}
                      {(notificationPayload?.request_reason || notificationPayload?.reason || notificationPayload?.requester_message || payload?.request_reason || payload?.reason || payload?.requester_message) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                          Reason: {notificationPayload?.request_reason || notificationPayload?.reason || notificationPayload?.requester_message || payload?.request_reason || payload?.reason || payload?.requester_message}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Show Respond button for pending requests, or status badge for responded requests */}
                  {(() => {
                    const requestId = notificationPayload?.access_request_id || payload?.access_request_id;
                    if (!requestId) return null;
                    
                    // Check status from notification payload first
                    const payloadStatus = notificationPayload?.status || payload?.status;
                    // Check status from fetched access request data
                    const requestStatus = accessRequestsStatus?.[requestId] as string | undefined;
                    const finalStatus = payloadStatus || requestStatus;
                    
                    // If status exists and is not pending, show status badge
                    if (finalStatus && finalStatus !== 'pending') {
                      return (
                        <div className="flex-shrink-0 self-start sm:self-auto">
                          {getStatusBadge(finalStatus)}
                        </div>
                      );
                    }
                    
                    // Otherwise show Respond button (pending or no status found)
                    return (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpen(false);
                          setSelectedRequestId(requestId);
                          setSelectedFileId(notificationPayload?.file_id || payload?.file_id);
                          setShowResponseModal(true);
                        }}
                        className="text-xs border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap transition-colors flex-shrink-0 self-start sm:self-auto"
                      >
                        Respond
                      </button>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Access Request Response - Recipient View (when their request was accepted/rejected) */}
            {actualType === 'access_request_responded' && (
              <div className="mt-2">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    {/* Show notification message (avoid duplication) */}
                    {notification.message ? (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                        {notification.message}
                      </p>
                    ) : (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                        Your access request for <strong className="break-all">{notificationPayload?.file_name || payload?.file_name}</strong> has been{' '}
                        <strong>{notificationPayload?.status || payload?.status || 'responded to'}</strong>
                      </p>
                    )}
                    {(notificationPayload?.response_message || payload?.response_message) && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                        Message: {notificationPayload?.response_message || payload?.response_message}
                      </p>
                    )}
                  </div>
                  {/* Show status badge on right side only (for recipients) */}
                  {(notificationPayload?.status || payload?.status) && (
                    <div className="flex-shrink-0 self-start sm:self-auto">
                      {getStatusBadge(notificationPayload?.status || payload?.status)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Access Request Canceled - File Owner View */}
            {actualType === 'access_request_canceled' && (
              <div className="mt-2">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    {notification.message ? (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                        {notification.message}
                      </p>
                    ) : (
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                        <strong>{notificationPayload?.requester_email || payload?.requester_email || payload?.user_email}</strong> has canceled their access request for{' '}
                        <strong className="break-all">{notificationPayload?.file_name || payload?.file_name}</strong>
                      </p>
                    )}
                  </div>
                  {/* Show status badge on right side only */}
                  <div className="flex-shrink-0 self-start sm:self-auto">
                    {getStatusBadge('canceled')}
                  </div>
                </div>
              </div>
            )}

            {type === 'new_device_registered' && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  A new <strong>{payload?.payload?.device_type || payload?.device_type}</strong> device <strong>"{payload?.payload?.device_name || payload?.device_name}"</strong> was added to your account
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                  Registered: {new Date(payload?.payload?.registered_at || payload?.registered_at).toLocaleString()}
                  {(payload?.payload?.ip_address || payload?.ip_address) && ` • IP: ${payload?.payload?.ip_address || payload?.ip_address}`}
                </p>
                <div className="mt-2">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      const deviceId = payload?.payload?.device_id || payload?.device_id;
                      if (deviceId) {
                        navigate(`/devices/${deviceId}`);
                      } else {
                        navigate('/devices');
                      }
                    }}
                    className="text-xs border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap transition-colors"
                  >
                    {t('details')}
                  </button>
                </div>
              </div>
            )}

            {type === 'file_access_revoked' && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  Your access to <strong className="break-all">{payload?.file_name}</strong> has been revoked
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                  Revoked by: {payload?.revoked_by_email}
                </p>
              </div>
            )}

            {type === 'policy_updated' && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  The policy <strong>{payload?.policy_name}</strong> has been updated
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                  Updated by: {payload?.updated_by_email}
                </p>
              </div>
            )}

            {type === 'user_removed_from_group' && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  You have been removed from the group <strong>{payload?.group_name}</strong>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                  Removed by: {payload?.removed_by_email}
                </p>
              </div>
            )}
            
            {type === 'system_announcement' && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  {payload?.message || 'System announcement'}
                </p>
                {payload?.maintenance_window && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                    Maintenance Window: {payload.maintenance_window}
                  </p>
                )}
                {payload?.duration && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                    Duration: {payload.duration}
                  </p>
                )}
              </div>
            )}

            {actualType === 'infrastructure_provisioning' && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  {payload?.message || notificationPayload?.message || 'Infrastructure provisioning in progress...'}
                </p>
                {notificationPayload?.url && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                    URL: {notificationPayload.url}
                  </p>
                )}
                {notificationPayload?.action_url && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {t('clickToViewInfrastructure')}
                  </p>
                )}
              </div>
            )}

            {actualType === 'license_alert' && (
              <div className="mt-2">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                  <strong>{notificationPayload?.company_name || payload?.company_name}</strong>
                </p>
                {(notificationPayload?.available_licenses !== undefined || payload?.available_licenses !== undefined) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                    {t('licensesLeft', { available: notificationPayload?.available_licenses ?? payload?.available_licenses, total: notificationPayload?.total_licenses ?? payload?.total_licenses })}
                  </p>
                )}
                {(notificationPayload?.company_id || payload?.company_id) && (
                  <div className="mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const companyId = notificationPayload?.company_id || payload?.company_id;
                        if (companyId) {
                          setIsOpen(false);
                          navigate(`/administration/licenses?company_id=${encodeURIComponent(companyId)}`);
                        }
                      }}
                      className="text-xs border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap transition-colors"
                    >
                      {t('details')}
                    </button>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none transition-colors ${
          isOpen ? '' : 'rounded-full'
        }`}
        title={t('title')}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 2a6 6 0 00-6 6c0 1.887-.454 3.665-1.257 5.234a.75.75 0 00.515 1.07 32.94 32.94 0 003.256.508 3.5 3.5 0 006.972 0 32.94 32.94 0 003.256-.508.75.75 0 00.515-1.07A11.717 11.717 0 0116 8a6 6 0 00-6-6zM8.05 14.943a33.54 33.54 0 003.9.557 3.5 3.5 0 01-6.9 0 33.54 33.54 0 003.9-.557z"
          />
        </svg>
        
        {/* Red Dot Indicator - only show when there are unread notifications */}
        {unreadCount > 0 && (
          <div className="absolute -top-0.5 -right-0.5 bg-red-500 rounded-full h-2.5 w-2.5 border-2 border-white dark:border-gray-900"></div>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="fixed sm:absolute right-0 top-16 sm:top-auto sm:mt-2 left-0 sm:left-auto w-full sm:w-[28rem] md:w-[32rem] sm:max-w-[32rem] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-[calc(100vh-5rem)] sm:max-h-96 overflow-hidden flex flex-col">
          <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{t('title')}</h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 whitespace-nowrap"
                  >
                    {t('markAllAsRead')}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <div className="text-4xl mb-2">🔔</div>
                <p>{t('noNotificationsYet')}</p>
                <p className="text-sm">{t('realTimeUpdatesHere')}</p>
              </div>
            ) : (
              processedNotifications.map((notification) =>
                renderNotification(notification)
              )
            )}
          </div>
        </div>
      )}

      {/* Access Request Response Modal */}
      {showResponseModal && selectedRequestId && (
        <AccessRequestResponseModal
          isOpen={showResponseModal}
          onClose={() => {
            setShowResponseModal(false);
            setSelectedRequestId(null);
            setSelectedFileId(null);
          }}
          requestId={selectedRequestId}
          fileId={selectedFileId || undefined}
          notificationId={notifications.find(n => {
            const np = n.payload?.payload || n.payload;
            return (np?.access_request_id || n.payload?.access_request_id) === selectedRequestId;
          })?.id}
          onResponded={() => {
            // Refresh notifications after responding
            // The notification context will automatically update via WebSocket or we can force a refresh
          }}
        />
      )}
    </div>
  );
};

export default NotificationCenter;
